import KaboodleToaster from "components/common/KaboodleToaster";
import { useCallback } from "react";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import { usePatchFileByIdMutation } from "services/file-api";
import { usePatchLienByIdMutation } from "services/lien-api";

const CLAIMANT_FIELDS = [
  "first_name",
  "middle_initial",
  "last_name",
  "gender",
  "address",
  "city",
  "state",
  "zip_code",
  "birth_date",
  "reasonable_expectation",
] as const;

const FILE_FIELDS = [
  "is_represented",
  "defense_counsel",
  "total_amount_disputed",
] as const;

type ClaimantField = (typeof CLAIMANT_FIELDS)[number];
type FileField = (typeof FILE_FIELDS)[number];

export const useLienSave = (
  lienId?: string,
  claimantId?: number,
  fileId?: number,
) => {
  const [patchClaimant] = usePatchClaimantByIdMutation();
  const [patchFile] = usePatchFileByIdMutation();
  const [patchLien] = usePatchLienByIdMutation();

  const handleClaimantSave = async (field: ClaimantField, value: unknown) => {
    if (!claimantId) return;

    await patchClaimant({
      id: claimantId,
      data: { [field]: value },
    }).unwrap();
  };

  const handleFileSave = async (field: FileField, value: unknown) => {
    if (!fileId) return;

    await patchFile({
      id: fileId.toString(),
      data: { [field]: value },
    }).unwrap();
  };

  const handleLienSave = async (field: string | null, value: unknown) => {
    if (!lienId) return;

    await patchLien({
      lienId,
      data: field ? { [field]: value } : value,
    }).unwrap();
  };

  return useCallback(
    async (field: string | null, value: unknown) => {
      try {
        if (!field) {
          return await handleLienSave(field, value);
        }

        if (CLAIMANT_FIELDS.includes(field as ClaimantField)) {
          return await handleClaimantSave(field as ClaimantField, value);
        }

        if (FILE_FIELDS.includes(field as FileField)) {
          return await handleFileSave(field as FileField, value);
        }

        return await handleLienSave(field, value);
      } catch (error) {
        console.error("Error saving:", error);
        KaboodleToaster.error("Error saving changes");
      }
    },
    [claimantId, fileId, lienId],
  );
};
